import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { WhatsAppSyncProgress } from '../types/whatsapp';

export function useWhatsAppSyncProgress() {
  const queryClient = useQueryClient();
  const [syncState, setSyncState] = useState<WhatsAppSyncProgress>({
    isActive: false,
    startedAt: null,
    progressPercent: 0,
    elapsedTime: 0,
    estimatedRemaining: 0,
    canDismiss: false,
  });

  const ESTIMATED_SYNC_DURATION = 22500; // 22.5s average (15-30s range)

  // Start sync tracking
  const startSync = useCallback(() => {
    const now = Date.now();
    setSyncState({
      isActive: true,
      startedAt: now,
      progressPercent: 0,
      elapsedTime: 0,
      estimatedRemaining: ESTIMATED_SYNC_DURATION,
      canDismiss: true,
    });

    // Store in localStorage for persistence across refreshes
    localStorage.setItem('whatsapp_sync_started_at', now.toString());
    localStorage.setItem('whatsapp_sync_active', 'true');
  }, [ESTIMATED_SYNC_DURATION]);

  // Complete sync tracking
  const completeSync = useCallback(() => {
    setSyncState(prev => ({
      ...prev,
      isActive: false,
      progressPercent: 100,
      estimatedRemaining: 0,
    }));

    localStorage.setItem('whatsapp_sync_active', 'false');
    localStorage.setItem('whatsapp_sync_completed_at', Date.now().toString());
  }, []);

  // Check if sync is complete (by detecting messages in DB)
  const checkSyncCompletion = useCallback(async () => {
    const chats = queryClient.getQueryData(['whatsapp', 'chats']) as any;

    // If we have chats with messages, sync is complete
    if (chats?.chats && chats.chats.length > 0) {
      const hasMessages = chats.chats.some((chat: any) =>
        chat.last_message_text || chat.unread_count > 0
      );

      if (hasMessages) {
        completeSync();
        return true;
      }
    }
    return false;
  }, [queryClient, completeSync]);

  // Update progress based on elapsed time
  useEffect(() => {
    if (!syncState.isActive || !syncState.startedAt) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = now - syncState.startedAt;
      const progress = Math.min(95, (elapsed / ESTIMATED_SYNC_DURATION) * 100);
      const remaining = Math.max(0, ESTIMATED_SYNC_DURATION - elapsed);

      setSyncState(prev => ({
        ...prev,
        elapsedTime: elapsed,
        progressPercent: progress,
        estimatedRemaining: remaining,
      }));

      // Check if sync is actually complete
      checkSyncCompletion();
    }, 500); // Update every 500ms

    return () => clearInterval(interval);
  }, [syncState.isActive, syncState.startedAt, ESTIMATED_SYNC_DURATION, checkSyncCompletion]);

  // Restore sync state on mount (if page was refreshed during sync)
  useEffect(() => {
    const storedStartTime = localStorage.getItem('whatsapp_sync_started_at');
    const storedActive = localStorage.getItem('whatsapp_sync_active');

    if (storedActive === 'true' && storedStartTime) {
      const startedAt = parseInt(storedStartTime, 10);
      const elapsed = Date.now() - startedAt;

      // If less than 60 seconds have passed, resume sync tracking
      if (elapsed < 60000) {
        setSyncState({
          isActive: true,
          startedAt,
          progressPercent: Math.min(95, (elapsed / ESTIMATED_SYNC_DURATION) * 100),
          elapsedTime: elapsed,
          estimatedRemaining: Math.max(0, ESTIMATED_SYNC_DURATION - elapsed),
          canDismiss: true,
        });
      } else {
        // Sync likely completed or failed
        localStorage.setItem('whatsapp_sync_active', 'false');
      }
    }
  }, [ESTIMATED_SYNC_DURATION]);

  // Handle network status changes
  useEffect(() => {
    const handleOnline = () => {
      if (syncState.isActive) {
        // Resume sync checking
        checkSyncCompletion();
      }
    };

    const handleOffline = () => {
      if (syncState.isActive) {
        // Pause progress updates but keep state
        console.warn('Network lost during sync');
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncState.isActive, checkSyncCompletion]);

  // Handle tab synchronization via storage events
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'whatsapp_sync_active' && e.newValue === 'false') {
        // Another tab completed the sync
        completeSync();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [completeSync]);

  return {
    syncState,
    startSync,
    completeSync,
    checkSyncCompletion,
  };
}
